const SOS = 0xda;
const EOI = 0xd9;
const APP1 = 0xe1;
const SOF_MARKERS = new Set(
  Array.from({ length: 16 }, (_, i) => 0xc0 + i).filter(
    (m) => m !== 0xc4 && m !== 0xc8 && m !== 0xcc,
  ),
);
const EXIF_PREFIX = Uint8Array.from([0x45, 0x78, 0x69, 0x66, 0x00, 0x00]);
const ORIENTATION_TAG = 0x0112;
const TYPE_SHORT = 3;
const SWAPPED = new Set([5, 6, 7, 8]);
const TAG_FOR_DEGREES: Record<90 | 180 | 270, number> = { 90: 6, 180: 3, 270: 8 };

interface Segment {
  marker: number;
  offset: number;
  length: number;
}

function byteAt(data: Uint8Array, i: number): number {
  const b = data[i];
  if (b === undefined) throw new Error(`byte ${i} out of range`);
  return b;
}

function startsWith(data: Uint8Array, prefix: Uint8Array): boolean {
  if (data.length < prefix.length) return false;
  return prefix.every((b, i) => data[i] === b);
}

function segments(data: Uint8Array): Segment[] {
  if (data.length < 2 || data[0] !== 0xff || data[1] !== 0xd8) {
    throw new Error("not a JPEG: missing SOI");
  }
  const found: Segment[] = [];
  let i = 2;
  while (i + 4 <= data.length) {
    if (data[i] !== 0xff) throw new Error(`expected a marker at byte ${i}`);
    while (i + 1 < data.length && data[i + 1] === 0xff) i += 1;
    if (i + 4 > data.length) throw new Error(`truncated JPEG at byte ${i}`);
    const marker = byteAt(data, i + 1);
    if (marker === SOS || marker === EOI) return found;
    const length = (byteAt(data, i + 2) << 8) | byteAt(data, i + 3);
    found.push({ marker, offset: i, length });
    i += 2 + length;
  }
  return found;
}

/** The SOF segment's own width/height, before any EXIF orientation swap. */
export function jpegEncodedSize(data: Uint8Array): { width: number; height: number } {
  for (const s of segments(data)) {
    if (!SOF_MARKERS.has(s.marker)) continue;
    if (s.offset + 9 > data.length) throw new Error("truncated SOF segment");
    return {
      height: (byteAt(data, s.offset + 5) << 8) | byteAt(data, s.offset + 6),
      width: (byteAt(data, s.offset + 7) << 8) | byteAt(data, s.offset + 8),
    };
  }
  throw new Error("no SOF segment: cannot read JPEG dimensions");
}

function tiffOrientation(tiff: Uint8Array): number {
  const view = new DataView(tiff.buffer, tiff.byteOffset, tiff.byteLength);
  let little: boolean;
  if (tiff[0] === 0x49 && tiff[1] === 0x49) little = true;
  else if (tiff[0] === 0x4d && tiff[1] === 0x4d) little = false;
  else throw new Error("bad TIFF byte order");
  const ifd0 = view.getUint32(4, little);
  const count = view.getUint16(ifd0, little);
  for (let index = 0; index < count; index++) {
    const entry = ifd0 + 2 + index * 12;
    if (view.getUint16(entry, little) !== ORIENTATION_TAG) continue;
    if (view.getUint16(entry + 2, little) !== TYPE_SHORT) {
      throw new Error("EXIF orientation is not a SHORT");
    }
    return view.getUint16(entry + 8, little);
  }
  return 1;
}

function orientationTag(data: Uint8Array): number {
  for (const s of segments(data)) {
    if (s.marker !== APP1) continue;
    const payload = data.subarray(s.offset + 4, s.offset + 2 + s.length);
    if (!startsWith(payload, EXIF_PREFIX)) continue;
    return tiffOrientation(payload.subarray(EXIF_PREFIX.length));
  }
  return 1;
}

export function jpegDisplaySize(data: Uint8Array): { width: number; height: number } {
  const { width, height } = jpegEncodedSize(data);
  return SWAPPED.has(orientationTag(data)) ? { width: height, height: width } : { width, height };
}

function stripExif(data: Uint8Array<ArrayBuffer>): Uint8Array<ArrayBuffer> {
  const parts: Uint8Array<ArrayBuffer>[] = [];
  let keptTo = 0;
  for (const s of segments(data)) {
    if (s.marker !== APP1) continue;
    if (!startsWith(data.subarray(s.offset + 4), EXIF_PREFIX)) continue;
    parts.push(data.subarray(keptTo, s.offset));
    keptTo = s.offset + 2 + s.length;
  }
  if (keptTo === 0) return data;
  parts.push(data.subarray(keptTo));
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let at = 0;
  for (const p of parts) {
    out.set(p, at);
    at += p.length;
  }
  return out;
}

// FF E1, length 0x22, "Exif\0\0", big-endian TIFF, one IFD0 entry: tag 0x0112 SHORT 1.
const EXIF_TEMPLATE = Uint8Array.from([
  0xff, 0xe1, 0x00, 0x22, 0x45, 0x78, 0x69, 0x66, 0x00, 0x00, 0x4d, 0x4d, 0x00, 0x2a, 0x00, 0x00,
  0x00, 0x08, 0x00, 0x01, 0x01, 0x12, 0x00, 0x03, 0x00, 0x00, 0x00, 0x01, 0x00, 0x00, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x00,
]);
const TEMPLATE_VALUE_OFFSET = 29;

export function withJpegOrientation(
  jpeg: Uint8Array<ArrayBuffer>,
  degrees: 0 | 90 | 180 | 270,
): Uint8Array<ArrayBuffer> {
  const stripped = stripExif(jpeg);
  if (degrees === 0) return stripped;
  const segment = EXIF_TEMPLATE.slice();
  segment[TEMPLATE_VALUE_OFFSET] = TAG_FOR_DEGREES[degrees];
  const out = new Uint8Array(stripped.length + segment.length);
  out.set(stripped.subarray(0, 2), 0);
  out.set(segment, 2);
  out.set(stripped.subarray(2), 2 + segment.length);
  return out;
}
